
import { create } from 'zustand';
import { HistoryItem, AthleteHistoryState } from './types';
import { getUserGymId } from '@/utils/gymAuth';
import {
  createAttendance,
  updateAttendance,
  getAttendanceByBookings,
} from '@/services/edgeFunctionWrappers/attendanceManagementWrapper';
import { getBookingHistory } from '@/services/edgeFunctionWrappers/bookingsManagementWrapper';
import i18n from '@/i18n';

export const useAthleteHistoryStore = create<AthleteHistoryState>((set, get) => ({
  history: [],
  isLoading: false,
  error: null,
  
  updateHistoryRating: async (
    attendance_id: string | null, 
    rating: number, 
    notes: string, 
    booking_id: string | null
  ) => {
    set({ isLoading: true });
  
    try {
      const gymId = getUserGymId();
      
      // ⚠️ Ordre volontaire : on privilégie `create_attendance` dès qu'un
      // `booking_id` est connu. Deux raisons :
      //  1. l'action serveur `create_attendance` est un UPSERT (elle met à jour
      //     la ligne existante pour ce booking) — pas de doublon possible ;
      //  2. `update_attendance` exige un rôle admin/coach côté edge function :
      //     un adhérent notant sa propre séance recevait un 403.
      if (booking_id) {
        const { data, error } = await createAttendance({
          booking_id,
          attended: true,
          rating,
          notes,
          gym_id: gymId,
        });

        if (error) throw error;
        // Un `data` nul signifie « rien n'a été écrit » (le wrapper renvoie
        // `{data:null,error:null}` sur session expirée) : ne pas l'annoncer
        // comme un succès.
        if (!data?.id) throw new Error(i18n.t('common:errors.attendanceNotSaved'));

        set((state: any) => ({
          history: state.history.map((item: any) =>
            item.id === booking_id
              ? { ...item, attendance_id: data.id, rating, notes }
              : item
          ),
          isLoading: false,
        }));

        return true;
      } else {
        if (!attendance_id) throw new Error("booking_id or attendance_id is required");

        const { data, error } = await updateAttendance(attendance_id, gymId, { rating, notes });

        if (error) throw error;
        if (!data?.id) throw new Error(i18n.t('common:errors.attendanceNotUpdated'));

        set((state: any) => ({
          history: state.history.map((item: any) =>
            item.attendance_id === attendance_id
              ? { ...item, rating, notes }
              : item
          ),
          isLoading: false,
        }));

        return true;
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du rating:', error);
      set({ isLoading: false });
      throw error;
    }
  },
  
  
  fetchHistory: async () => {
    set({ isLoading: true, error: null });
  
    try {
      const gymId = getUserGymId();
      if (!gymId) throw new Error('No gym selected');

      // Use the edge function wrapper
      const { data, error } = await getBookingHistory(gymId);

      if (error) throw error; 
      if (!data || data.length === 0) {
        set({ history: [], isLoading: false });
        return;
      }
  
      // ========== FORMAT OUTPUT ==========
      const historyItems: HistoryItem[] = data.map((item: any) => ({
        id: item.booking_id,
        title: item.class_name || "Unknown",
        date: new Date(item.class_date),
        time: item.class_time || "",
        duration: parseInt(item.class_duration || "0"),
        coach: item.default_instructor || "",
        assignedCoach: item.coach_first_name
          ? `${item.coach_first_name} ${item.coach_last_name}`
          : null,
        status: item.status,
        rating: item.rating,
        notes: item.notes,
        // Rempli juste après : la RPC `get_booking_history` ne renvoie pas
        // `ca.id`, d'où le `null` codé en dur d'origine. Sans identifiant,
        // `editingId !== item.attendance_id` comparait null à null et toute
        // séance déjà notée réaffichait le formulaire (audit 4.2).
        attendance_id: null
      }));

      // Hydratation des identifiants de présence en un appel groupé.
      // Non bloquant : en cas d'échec on garde l'historique, seule l'édition
      // fine du commentaire retombe sur l'upsert par booking_id.
      const bookingIds = historyItems.map((h) => h.id).filter(Boolean);
      if (bookingIds.length > 0) {
        try {
          const { data: attendances, error: attendanceError } =
            await getAttendanceByBookings(gymId, bookingIds);
          if (attendanceError) throw attendanceError;

          const byBooking = new Map<string, string>();
          (attendances || []).forEach((a) => {
            if (a?.booking_id && a?.id) byBooking.set(a.booking_id, a.id);
          });
          historyItems.forEach((h) => {
            h.attendance_id = byBooking.get(h.id) ?? null;
          });
        } catch (attendanceError) {
          console.warn(
            "[athleteHistory] attendance ids unavailable, falling back to booking_id upsert",
            attendanceError
          );
        }
      }

      set({ history: historyItems, isLoading: false });

    } catch (error) {
      console.error("Error fetching athlete history:", error);
      set({ error: "Failed to fetch history", isLoading: false });
    }
  },  
  
  
  
  addRating: (id: string, rating: number) => {
    const history = get().history;
    const updatedHistory = history.map(item => 
      item.id === id ? { ...item, rating } : item
    );
    set({ history: updatedHistory });
  }
}));
